'use client';

import { useCommandMenu } from '@/contexts/command-menu-context';
import { api, useQuery } from '@/lib/convex';
import { SHORTCUTS } from '@/lib/shortcuts';
import { isEditableShortcutTarget } from '@/lib/utils';
import Mousetrap from 'mousetrap';
import { useParams } from 'next/navigation';
import { useRouter } from 'nextjs-toploader/app';
import { useEffect, useRef } from 'react';

/**
 * Binds the global shortcut registry (G-then-X navigation sequences, ⌘J)
 * via Mousetrap. ⌘K itself stays in the command-menu context — it needs the
 * allow-inside-command-input window listener.
 */
export function GlobalShortcuts() {
  const router = useRouter();
  const params = useParams();
  const orgSlug = params?.orgSlug as string | undefined;
  const { data: viewer } = useQuery(api.users.queries.viewer);
  const { isOpen } = useCommandMenu();
  const isNavigating = useRef(false);
  const menuOpenRef = useRef(false);

  useEffect(() => {
    menuOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!viewer) return;

    const bound: string[] = [];

    for (const shortcut of SHORTCUTS) {
      if (shortcut.scope === 'org' && !orgSlug) continue;
      if (!shortcut.href) continue;

      const href = shortcut.href(orgSlug ?? '');
      const handler = (e?: KeyboardEvent) => {
        if (!e) return false;
        if (menuOpenRef.current) return false;
        if (isNavigating.current) return false;
        if (isEditableShortcutTarget(e.target)) return false;

        e.preventDefault();
        e.stopPropagation();

        isNavigating.current = true;
        router.push(href);
        // Reset after a short delay so rapid repeats don't stack navigations.
        setTimeout(() => {
          isNavigating.current = false;
        }, 500);
        return false;
      };

      Mousetrap.bind(shortcut.sequence, handler);
      bound.push(shortcut.sequence);
    }

    return () => {
      for (const sequence of bound) {
        Mousetrap.unbind(sequence);
      }
    };
  }, [viewer, orgSlug, router]);

  return null;
}
